import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { FC, ReactNode, useCallback } from 'react';
import { Tooltip } from 'react-tooltip';
import { render } from 'storyblok-rich-text-react-renderer-ts';

import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import { getFirstStoryblokParagraph } from '../../utils/renderStoryblokRichText';
import { useGlossary } from './GlossaryContext';

interface Props {
  children: ReactNode;
  href: string;
}

const SpanParagraph = (children: ReactNode) => <span>{children}</span>;

export const GlossaryLink: FC<Props> = ({ children, href }) => {
  const { t } = useTranslation();
  const { analytics, glossary } = useGlossary();
  const [analyticsEmitter] = useAnalyticsEmitter(analytics || {});

  const term = glossary.find((g) => g.href === href);

  const handleTermClick = useCallback(() => {
    analyticsEmitter('glossary', 'term popup link click', {
      term: term?.title,
    });
    window.open(term?.href, '_blank');
  }, [analyticsEmitter, term]);

  const handleTooltipOpen = useCallback(() => {
    analyticsEmitter('glossary', 'term popup open', { term: term?.title });
  }, [analyticsEmitter, term]);

  if (!term) {
    return (
      <Link className="glossary-link" href={href} target="_blank">
        {children}
      </Link>
    );
  }

  return (
    <>
      <Link
        className="glossary-link"
        href={href}
        id={`glossary-${term.slug}`}
        onClick={(ev) => ev.preventDefault()}
        target="_blank"
      >
        {children}
      </Link>
      <Tooltip
        afterShow={handleTooltipOpen}
        anchorSelect={`#glossary-${term.slug}`}
        className="glossaryTooltip"
        classNameArrow="glossaryArrow"
        clickable
        variant="light"
        wrapper="span"
      >
        <span className="flex flex-col items-start indent-0">
          <span className="mb-2 font-bold">{term.title}</span>
          <span className="mb-3">
            {render(getFirstStoryblokParagraph(term.description), {
              nodeResolvers: {
                paragraph: SpanParagraph,
              },
            })}
          </span>
          <button className="hyperlink" onClick={handleTermClick} type="button">
            {t('readMore')}
          </button>
        </span>
      </Tooltip>
    </>
  );
};
